"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import type {
  CandidateEditableProfileDto,
  CandidateSessionResponseDto,
} from "@artha/api-contracts";
import {
  RESUME_UPLOAD_MAX_BYTES,
  RESUME_UPLOAD_MIME_TYPES,
  type ResumeUploadMimeType,
} from "@artha/api-contracts";
import {
  clearCandidateIdentity,
  readCandidateIdentity,
  writeCandidateIdentity,
  type CandidateIdentityStorage,
} from "../services/storage";
import {
  fetchCandidateSession,
  logoutCandidate,
  requestCandidateVerification,
  uploadResumeForPersonalization,
  verifyCandidateCode,
} from "../services/personalizationClient";
import { isResumePersonalizationUiEnabled } from "../personalization.constants";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

type VerificationMode = "profile" | "login";

export const useCandidatePersonalization = (creatorId?: string) => {
  const t = useTranslations("profile.resumePersonalization");
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  useEffect(() => {
    posthogRef.current = posthog;
  }, [posthog]);
  const [identity, setIdentity] = useState<CandidateIdentityStorage>(
    readCandidateIdentity,
  );
  const [editableProfile, setEditableProfile] =
    useState<CandidateEditableProfileDto | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasResume, setHasResume] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(
    isResumePersonalizationUiEnabled,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [verificationMode, setVerificationMode] =
    useState<VerificationMode>("profile");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [jobsRefreshVersion, setJobsRefreshVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const capturePersonalizationEvent = useCallback(
    (
      event: string,
      properties: Record<string, string | boolean | undefined> = {},
    ) => {
      posthogRef.current?.capture?.(event, {
        ...(creatorId && { portal_id: creatorId }),
        ...properties,
      });
    },
    [creatorId],
  );

  const applySession = useCallback(
    (session: CandidateSessionResponseDto, refreshJobs: boolean) => {
      if (!session.authenticated || !session.externalId) return;
      const nextIdentity: CandidateIdentityStorage = {
        externalId: session.externalId,
        resumePersonalized: session.hasResume,
        candidatePreferences: session.preferences ?? null,
      };
      setIdentity(nextIdentity);
      writeCandidateIdentity(nextIdentity);
      setEditableProfile(session.editableProfile ?? null);
      setHasResume(session.hasResume);
      setIsAuthenticated(true);
      if (refreshJobs && session.hasResume) {
        setJobsRefreshVersion((version) => version + 1);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isResumePersonalizationUiEnabled) return;

    let active = true;
    void fetchCandidateSession()
      .then((session) => {
        if (!active) return;
        if (session.authenticated) {
          applySession(session, true);
        } else {
          setIdentity({
            externalId: null,
            resumePersonalized: false,
            candidatePreferences: null,
          });
          clearCandidateIdentity();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsSessionLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applySession]);

  const getPosthogDistinctId = useCallback(() => {
    const distinctId = posthog?.get_distinct_id?.();
    if (!distinctId) {
      throw new Error(t("errors.identityNotReady"));
    }
    return distinctId;
  }, [posthog, t]);

  const uploadResume = useCallback(
    async (file: File) => {
      if (!creatorId) throw new Error(t("errors.creatorUnavailable"));
      if (
        !RESUME_UPLOAD_MIME_TYPES.includes(file.type as ResumeUploadMimeType) ||
        file.size > RESUME_UPLOAD_MAX_BYTES
      ) {
        const message = t("errors.unsupportedResume");
        setError(message);
        throw new Error(message);
      }

      setIsUploading(true);
      setError(null);
      try {
        const result = await uploadResumeForPersonalization({
          file,
          creatorId,
          posthogDistinctId: getPosthogDistinctId(),
          externalId: identity.externalId || undefined,
        });
        if (result.parserStatus !== "success" || !result.editableProfile) {
          throw new Error(
            t("errors.resumeUnreadable"),
          );
        }
        const nextIdentity = {
          ...identity,
          externalId: result.externalId,
        };
        setIdentity(nextIdentity);
        writeCandidateIdentity(nextIdentity);
        setEditableProfile(result.editableProfile);
        setHasResume(true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_UPLOAD, {
          file_type: file.type,
          replacing_resume: hasResume,
        });
        return result;
      } catch (uploadError) {
        const message =
          uploadError instanceof Error
            ? uploadError.message
            : t("errors.resumeUploadFailed");
        setError(message);
        throw uploadError;
      } finally {
        setIsUploading(false);
      }
    },
    [
      capturePersonalizationEvent,
      creatorId,
      getPosthogDistinctId,
      hasResume,
      identity,
      t,
    ],
  );

  const requestProfileVerification = useCallback(
    async (profile: CandidateEditableProfileDto) => {
      if (!identity.externalId) {
        throw new Error(t("errors.uploadFirst"));
      }
      setIsRequestingCode(true);
      setError(null);
      try {
        const result = await requestCandidateVerification({
          externalId: identity.externalId,
          posthogDistinctId: getPosthogDistinctId(),
          editableProfile: profile,
        });
        setEditableProfile(profile);
        setVerificationMode("profile");
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
        }
        return result.verificationRequired;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [
      applySession,
      getPosthogDistinctId,
      identity.externalId,
      t,
    ],
  );

  const requestSignIn = useCallback(
    async (email: string) => {
      setIsRequestingCode(true);
      setError(null);
      try {
        const result = await requestCandidateVerification({ email });
        setVerificationMode("login");
        setMaskedEmail(result.maskedEmail);
        if (!result.verificationRequired) {
          applySession(result, true);
          capturePersonalizationEvent(
            ANALYTICS_EVENTS.AUD_RESUME_LOGIN,
            {
              has_resume: result.hasResume,
              email: (result.editableProfile?.email || email)
                .trim()
                .toLowerCase(),
            },
          );
        }
        return result.verificationRequired;
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : t("errors.verificationSendFailed");
        setError(message);
        throw requestError;
      } finally {
        setIsRequestingCode(false);
      }
    },
    [applySession, capturePersonalizationEvent, t],
  );

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      setIsVerifying(true);
      setError(null);
      try {
        const posthogDistinctId = getPosthogDistinctId();
        const result = await verifyCandidateCode({
          email,
          code,
          posthogDistinctId,
          ...(verificationMode === "profile" && identity.externalId
            ? {
                externalId: identity.externalId,
              }
            : {}),
        });
        applySession(result, true);
        capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_VERIFICATION, {
          mode: verificationMode,
          has_resume: result.hasResume,
        });
        if (verificationMode === "login") {
          capturePersonalizationEvent(
            ANALYTICS_EVENTS.AUD_RESUME_LOGIN,
            {
              has_resume: result.hasResume,
              email: (result.editableProfile?.email || email)
                .trim()
                .toLowerCase(),
            },
          );
        }
        return result;
      } catch (verificationError) {
        const message =
          verificationError instanceof Error
            ? verificationError.message
            : t("errors.verificationFailed");
        setError(message);
        throw verificationError;
      } finally {
        setIsVerifying(false);
      }
    },
    [
      applySession,
      capturePersonalizationEvent,
      getPosthogDistinctId,
      identity.externalId,
      t,
      verificationMode,
    ],
  );

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setError(null);
    try {
      await logoutCandidate();
      capturePersonalizationEvent(ANALYTICS_EVENTS.AUD_RESUME_LOGOUT, {
        ...(editableProfile?.email && {
          email: editableProfile.email.trim().toLowerCase(),
        }),
      });
      clearCandidateIdentity();
      setIdentity({
        externalId: null,
        resumePersonalized: false,
        candidatePreferences: null,
      });
      setEditableProfile(null);
      setHasResume(false);
      setIsAuthenticated(false);
      setMaskedEmail("");
      posthog?.reset?.();
      setJobsRefreshVersion((version) => version + 1);
    } catch (logoutError) {
      const message =
        logoutError instanceof Error
          ? logoutError.message
          : t("errors.logoutFailed");
      setError(message);
      throw logoutError;
    } finally {
      setIsLoggingOut(false);
    }
  }, [capturePersonalizationEvent, editableProfile, posthog, t]);

  return {
    externalId: isAuthenticated ? identity.externalId : null,
    isAuthenticated,
    isSessionLoading,
    isPersonalized: isAuthenticated && hasResume,
    hasResume,
    candidatePreferences: isAuthenticated
      ? identity.candidatePreferences
      : null,
    editableProfile,
    maskedEmail,
    verificationMode,
    isUploading,
    isRequestingCode,
    isVerifying,
    isLoggingOut,
    jobsRefreshVersion,
    error,
    uploadResume,
    requestProfileVerification,
    requestSignIn,
    verifyCode,
    logout,
  };
};

export type CandidatePersonalizationController = ReturnType<
  typeof useCandidatePersonalization
>;
